import { TranslationKey } from './../../../types/translation/TranslationKey';
import React, { useContext } from 'react';
import { Route, Routes } from 'react-router-dom';
import ProductOverviewPane from './Product/ProductOverviewPane';
import ProductHistoryOverviewPane from './History/ProductHistoryOverviewPane';
import { CompanyContext } from '../../../context/CompanyContext';
import BinOverviewPane from './Bin/BinOverviewPane';
import { StockLocationContext } from '../../../context/StockLocationContext';
import { StockLocationRoleAssignmentContext } from '../../../context/StockLocationRoleAssignmentContext';
import { StockLocationRole } from '../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import { testIds } from '../../../util/identifiers/identifiers.util';
import { t } from '../../../types/translation/Translator';
import Pane from '../../../VentoryUI/components/common/Pane';
import ContainerOverviewPane from './Container/ContainerOverviewPane';
import LotOverviewPane from './Lot/LotOverviewPane';
import ProductSettingsScreen from './Product/Settings/ProductSettingsScreen';
import ProductTransactionSettingsScreen from './History/Settings/ProductTransactionSettingsScreen';
import LotDetailPane from './Lot/LotDetailPane';
import BinDetailPane from './Bin/BinDetailPane';
import ContainerDetailScreen from './Container/Detail/ContainerDetailScreen';
import CreateContainerScreen from './Container/Detail/CreateContainerScreen';
import { Routing } from '../../../util/routes.util';
import Provider from '../../../providers/common/Provider';
import ProductDetailPane from './Product/Detail/ProductDetailPane';
import BinSettingsScreen from './Bin/Settings/BinSettingsScreen';
import ContainerSettingsScreen from './Container/Settings/ContainerSettingsScreen';
import LotSettingsScreen from './Lot/Settings/LotSettingsScreen';
import CreateOrderFromStockModalProvider from '../../../providers/CreateOrderFromStockModalProvider';

export default function StockScreen() {
  return (
    <StockScreenProvider>
      <_StockScreen />
    </StockScreenProvider>
  );
}

function StockScreenProvider({ children }: { children: React.ReactNode }) {
  return (
    <Provider
      barcodeExportConfiguration
      bin
      binStatus
      binConstraint
      container
      contact
      containerType
      csvUploadConfiguration
      customField
      handlingUnit
      lot
      order
      file
      product
      productExportConfiguration
      productMasterData
      tableSettings
      productTransaction
      productTransactionExportConfiguration
      stockLocation
      stockLocationRoleAssignment
      task
      tag
      tagRelation
      kitTemplates
      kit
    >
      <CreateOrderFromStockModalProvider>{children}</CreateOrderFromStockModalProvider>
    </Provider>
  );
}

function _StockScreen() {
  const { currentCompany } = useContext(CompanyContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);
  const { stockLocations } = useContext(StockLocationContext);

  const tabs = [
    {
      text: t(TranslationKey.product).plural.label,
      path: Routing.stock.products.overview(),
      key: 'products',
    },
    {
      text: t(TranslationKey.bin).plural.label,
      path: Routing.stock.bins.overview(),
      key: 'bins',
    },
    {
      text: t(TranslationKey.container).plural.label,
      path: Routing.stock.containers.overview(),
      key: 'containers',
    },
    {
      text: t(TranslationKey.lot).plural.label,
      path: Routing.stock.lots.overview(),
      key: 'lots',
    },
    {
      text: t(TranslationKey.history).singular.label,
      path: Routing.stock.history.overview(),
      key: 'history',
    },
  ];

  const displayContainers = currentCompany.settings.featureToggles.containers.containers;
  const displayLots = currentCompany.settings.featureToggles.lots.lots;

  const displayHistory =
    currentCompany.settings.featureToggles.productTransactions.web &&
    [...stockLocations.values()].filter(sl =>
      hasStockLocationRole(currentCompany.id, sl.id, StockLocationRole.STOCK_LOCATION_MANAGER),
    ).length;

  const filteredTabs = tabs.filter(item => {
    switch (item.key) {
      case 'history':
        return displayHistory;
      case 'containers':
        return displayContainers;
      case 'lots':
        return displayLots;
    }

    return true;
  });

  return (
    <Routes>
      <Route path='products/:id/*' element={<ProductDetailPane />} />
      <Route path='products/settings/*' element={<ProductSettingsScreen />} />

      <Route path='bins/:id/*' element={<BinDetailPane />} />
      <Route path='bins/settings/*' element={<BinSettingsScreen />} />

      {displayLots ? (
        <>
          <Route path='lots/:id/*' element={<LotDetailPane />} />
          <Route path='lots/settings/*' element={<LotSettingsScreen />} />
        </>
      ) : null}

      {displayContainers ? (
        <>
          <Route path='containers/create/*' element={<CreateContainerScreen />} />
          <Route path='containers/:id/*' element={<ContainerDetailScreen />} />
          <Route path='containers/settings/*' element={<ContainerSettingsScreen />} />
        </>
      ) : null}

      <Route path='history/settings/*' element={<ProductTransactionSettingsScreen />} />

      <Route
        path={'*'}
        element={
          <Pane tabs={filteredTabs} testId={testIds.stockScreen}>
            <Routes>
              <Route path='products' element={<ProductOverviewPane />} />
              <Route path='bins' element={<BinOverviewPane />} />
              {displayLots ? <Route path='lots' element={<LotOverviewPane />} /> : null}
              {displayContainers ? <Route path='containers' element={<ContainerOverviewPane />} /> : null}
              <Route path='history' element={<ProductHistoryOverviewPane />} />
            </Routes>
          </Pane>
        }
      />
    </Routes>
  );
}
